import React, {useMemo} from 'react';
import styled from 'styled-components';
import {TFunction, useTranslation} from 'next-i18next';
import DataProtectionFAQItem from './DataProtectionFAQItem';
import {WidthContainer} from '../landing/LandingComponents';
import {panelistaPrimaryColor} from 'src/utils/constants';
import {MainTitle} from '../landingStyledComponents';
import RemixIcon from 'src/components/RemixIcon';

const compactThreshold = 900;

const StyledWidthContainer = styled(WidthContainer)`
  display: flex;

  @media (max-width: ${compactThreshold}px) {
    padding: 0 var(--side-padding);
  }
`;

const CenterContentSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const Description = styled.p`
  max-width: 650px;
  text-align: center;
  font-size: 20px;
  margin: 80px 0;

  > a {
    color: ${panelistaPrimaryColor};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${compactThreshold}px) {
    font-size: 16px;
    margin: 60px 0;
  }
`;

const ListContainer = styled.div`
  max-width: 760px;
  width: 100%;
  margin-top: 40px;
`;

const ListTitle = styled(MainTitle)`
  margin-top: 10px;
  font-size: 24px;
  text-align: center;
  letter-spacing: 0px;

  @media (max-width: ${compactThreshold}px) {
    margin-top: 20px;
    font-size: 20px;
  }
`;

const FAQItem = styled.div<{isActive?: boolean}>`
  border: none;
  background-color: transparent;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: normal;
  text-align: left;
  border-bottom: 1px solid rgb(0, 0, 0, 0.1);
  flex-direction: column;

  p {
    color: ${(props) => (props.isActive ? panelistaPrimaryColor : 'var(--dark-text)')};
  }

  &:hover p {
    color: ${panelistaPrimaryColor};
  }

  & > span {
    font-size: 20px;
    margin-top: 8px;
  }

  & svg {
    transform: rotate(${(props) => (props.isActive ? '180deg' : '0deg')});
  }
`;

const LinkItem = styled(FAQItem)`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  text-decoration: none;

  p {
    transition: color 0.2s;
  }

  &:hover p {
    color: ${panelistaPrimaryColor};
    transition: color 0.2s;
  }
`;

const LinkText = styled.p`
  font-family: var(--font-jakarta);
  font-weight: 700;
  font-size: 20px;
  padding: 24px 0;

  @media (max-width: ${compactThreshold}px) {
    font-size: 16px;
  }
`;

const getFaqContent = (t: TFunction) => [
  {
    title: t('faq.item one title'),
    body: t('faq.item one body')
  },
  {
    title: t('faq.item two title'),
    body: t('faq.item two body')
  },
  {
    title: t('faq.item three title'),
    body: t('faq.item three body')
  },
  {
    title: t('faq.item four title'),
    body: t('faq.item four body')
  },
  {
    title: t('faq.item five title'),
    body: t('faq.item five body')
  },
  {
    title: t('faq.item six title'),
    body: t('faq.item six body')
  },
  {
    title: t('faq.item seven title'),
    body: t('faq.item seven body')
  },
  {
    title: t('faq.item eight title'),
    body: t('faq.item eight body')
  },
  {
    title: t('faq.item nine title'),
    body: t('faq.item nine body')
  }
];

const DataProtectionFAQSection = () => {
  const {t, i18n} = useTranslation();

  const faqContent = useMemo(() => getFaqContent(t), [i18n.language]);

  return (
    <StyledWidthContainer>
      <CenterContentSection>
        {i18n.language !== 'sv' ? null : (
          <>
            <ListTitle>{t('website.frequently asked questions')}</ListTitle>
            <ListContainer>
              {faqContent.map((article, i) => (
                <DataProtectionFAQItem key={i} article={article} />
              ))}
              <DataProtectionFAQItem
                article={{
                  title: t('faq.item ten title'),
                  image: <img src="/images/dataDiagram.png" alt={t('faq.item ten title')} style={{maxWidth: '100%'}} />
                }}
              />
            </ListContainer>
          </>
        )}

        <ListTitle>{t('website.relevant documents')}</ListTitle>
        <ListContainer>
          <LinkItem as="a" href="/legal/general-terms" target="_blank">
            <LinkText>{t('website.panelista general terms')}</LinkText>
            <RemixIcon name="arrow-right-s-line" color="#54C7CD" />
          </LinkItem>
          <LinkItem as="a" href="/legal/dpa" target="_blank">
            <LinkText>{t('website.panelista standard agreement')}</LinkText>
            <RemixIcon name="arrow-right-s-line" color="#54C7CD" />
          </LinkItem>
          <LinkItem as="a" href="/legal/privacy-policy" target="_blank">
            <LinkText>{t('website.panelista privacy policy')}</LinkText>
            <RemixIcon name="arrow-right-s-line" color="#54C7CD" />
          </LinkItem>
          <LinkItem as="a" href="/legal/terms-of-service" target="_blank">
            <LinkText>{t('website.panelista tos')}</LinkText>
            <RemixIcon name="arrow-right-s-line" color="#54C7CD" />
          </LinkItem>
        </ListContainer>

        <Description
          style={{fontWeight: '800'}}
          dangerouslySetInnerHTML={{
            __html: t('website.send questions', {email: '<a href="mailto:coach@panelista.com">coach@panelista.com</a>'})
          }}
        />
      </CenterContentSection>
    </StyledWidthContainer>
  );
};

export default DataProtectionFAQSection;
